/**
 * One-sample, independent two-sample (Welch or Student) and paired t-tests.
 *
 * Matches `scipy.stats.ttest_1samp`, `ttest_ind(equal_var=False|True)` and `ttest_rel`
 * (and R's `t.test`): the same t, the same Welch–Satterthwaite df, the same p-values and
 * the same two-sided confidence interval — for the mean in the one-sample case, for the
 * mean difference otherwise.
 *
 * Effect sizes, and the standardiser each one uses:
 * - one-sample: d = (x̄ − μ₀) / s;
 * - Student: d = (x̄₁ − x̄₂) / s_p, the pooled SD;
 * - Welch: d = (x̄₁ − x̄₂) / √((s₁² + s₂²) / 2), the root of the average variance — the
 *   pooled SD assumes the equal variances Welch's test does not;
 * - paired: d_z = d̄ / s_d, the SD of the differences.
 */

import { mean, variance } from "@/lib/stats/descriptive";
import {
  studentTCdf,
  studentTQuantile,
  studentTTwoTailed,
  studentTUpper,
} from "@/lib/stats/distributions";

export interface SampleSummary {
  n: number;
  mean: number;
  sd: number;
}

export type TTestFailure =
  | "too-few"
  | "no-variance"
  | "length-mismatch"
  | "invalid-summary";

export type TTestKind = "one-sample" | "welch" | "student" | "paired";

export interface TTestResult {
  kind: TTestKind;
  t: number;
  df: number;
  /** x̄ − μ₀, x̄₁ − x̄₂ or d̄. */
  difference: number;
  se: number;
  pTwoSided: number;
  pLess: number;
  pGreater: number;
  /** Two-sided interval at `level`: for the mean (one-sample) or the mean difference. */
  ciLow: number;
  ciHigh: number;
  level: number;
  tCritical: number;
  effectSize: number;
  /** The samples as summarised (one for one-sample and paired, two otherwise). */
  samples: SampleSummary[];
  /** Paired: the summary of the differences. */
  differences?: SampleSummary;
  /** One-sample: the hypothesised mean. */
  mu0?: number;
  /** Student: the pooled SD. */
  pooledSd?: number;
  /** Welch: s₁²/n₁ and s₂²/n₂, the two halves of the squared SE. */
  varianceTerms?: [number, number];
}

export type TTestOutcome =
  | { ok: true; result: TTestResult }
  | { ok: false; reason: TTestFailure };

export function summarize(values: readonly number[]): SampleSummary {
  return {
    n: values.length,
    mean: mean(values),
    sd: values.length > 1 ? Math.sqrt(variance(values)) : Number.NaN,
  };
}

function validSummary(sample: SampleSummary): boolean {
  return (
    Number.isFinite(sample.mean) &&
    Number.isFinite(sample.sd) &&
    sample.sd >= 0 &&
    Number.isInteger(sample.n)
  );
}

interface Core {
  t: number;
  df: number;
  se: number;
  estimate: number;
  level: number;
}

type Extra = Pick<
  TTestResult,
  "kind" | "samples" | "differences" | "mu0" | "pooledSd" | "varianceTerms"
>;

function finish(
  core: Core,
  difference: number,
  effectSize: number,
  extra: Extra
): TTestOutcome {
  const { t, df, se, estimate, level } = core;
  const tCritical = studentTQuantile(1 - (1 - level) / 2, df);
  return {
    ok: true,
    result: {
      ...extra,
      t,
      df,
      difference,
      se,
      pTwoSided: studentTTwoTailed(t, df),
      pLess: studentTCdf(t, df),
      pGreater: studentTUpper(t, df),
      ciLow: estimate - tCritical * se,
      ciHigh: estimate + tCritical * se,
      level,
      tCritical,
      effectSize,
    },
  };
}

export function oneSampleTTest(
  sample: SampleSummary,
  mu0: number,
  level = 0.95
): TTestOutcome {
  if (!(validSummary(sample) && Number.isFinite(mu0))) {
    return { ok: false, reason: "invalid-summary" };
  }
  if (sample.n < 2) {
    return { ok: false, reason: "too-few" };
  }
  if (sample.sd === 0) {
    return { ok: false, reason: "no-variance" };
  }
  const se = sample.sd / Math.sqrt(sample.n);
  const difference = sample.mean - mu0;
  return finish(
    { t: difference / se, df: sample.n - 1, se, estimate: sample.mean, level },
    difference,
    difference / sample.sd,
    { kind: "one-sample", samples: [sample], mu0 }
  );
}

/** The Welch–Satterthwaite degrees of freedom. */
export function welchDf(a: SampleSummary, b: SampleSummary): number {
  const va = (a.sd * a.sd) / a.n;
  const vb = (b.sd * b.sd) / b.n;
  return (va + vb) ** 2 / (va ** 2 / (a.n - 1) + vb ** 2 / (b.n - 1));
}

export function independentTTest(
  a: SampleSummary,
  b: SampleSummary,
  equalVariances: boolean,
  level = 0.95
): TTestOutcome {
  if (!(validSummary(a) && validSummary(b))) {
    return { ok: false, reason: "invalid-summary" };
  }
  if (a.n < 2 || b.n < 2) {
    return { ok: false, reason: "too-few" };
  }
  const difference = a.mean - b.mean;
  const va = (a.sd * a.sd) / a.n;
  const vb = (b.sd * b.sd) / b.n;
  if (equalVariances) {
    const df = a.n + b.n - 2;
    const pooledVariance =
      ((a.n - 1) * a.sd * a.sd + (b.n - 1) * b.sd * b.sd) / df;
    if (pooledVariance === 0) {
      return { ok: false, reason: "no-variance" };
    }
    const pooledSd = Math.sqrt(pooledVariance);
    const se = pooledSd * Math.sqrt(1 / a.n + 1 / b.n);
    return finish(
      { t: difference / se, df, se, estimate: difference, level },
      difference,
      difference / pooledSd,
      { kind: "student", samples: [a, b], pooledSd }
    );
  }
  if (va + vb === 0) {
    return { ok: false, reason: "no-variance" };
  }
  const se = Math.sqrt(va + vb);
  const df = welchDf(a, b);
  const averageSd = Math.sqrt((a.sd * a.sd + b.sd * b.sd) / 2);
  return finish(
    { t: difference / se, df, se, estimate: difference, level },
    difference,
    difference / averageSd,
    { kind: "welch", samples: [a, b], varianceTerms: [va, vb] }
  );
}

export function pairedTTest(
  first: readonly number[],
  second: readonly number[],
  level = 0.95
): TTestOutcome {
  if (first.length !== second.length) {
    return { ok: false, reason: "length-mismatch" };
  }
  if (first.length < 2) {
    return { ok: false, reason: "too-few" };
  }
  const diffs = first.map((value, index) => value - second[index]);
  const differences = summarize(diffs);
  if (differences.sd === 0) {
    return { ok: false, reason: "no-variance" };
  }
  const se = differences.sd / Math.sqrt(differences.n);
  return finish(
    {
      t: differences.mean / se,
      df: differences.n - 1,
      se,
      estimate: differences.mean,
      level,
    },
    differences.mean,
    differences.mean / differences.sd,
    {
      kind: "paired",
      samples: [summarize(first), summarize(second)],
      differences,
    }
  );
}

/** The p-value for the chosen alternative. */
export function pFor(
  result: Pick<TTestResult, "pTwoSided" | "pLess" | "pGreater">,
  alternative: "two-sided" | "less" | "greater"
): number {
  if (alternative === "less") {
    return result.pLess;
  }
  if (alternative === "greater") {
    return result.pGreater;
  }
  return result.pTwoSided;
}
